import clsx from "clsx";
import React from "react";
import {Card, CardLayout} from "../Card/Card";
import {CardImagePosition, HeroCardImageAlignment, HeroCardImageSize} from "../Card/CardImage";
import {HorizontalAlignment, LayoutWidth, MediaSpecific} from "../Layout";
import {PageContentBlock} from "../Page/PageContentBlock";
import {isReactNodeEmpty} from "../Utils";
import css from "./image-card.module.scss";
import VadImage from "vadimages-nextjs-image-optimizer";
import { InplaceVideo, Patent } from '@/components';
interface ImageCardAlignProps
{
    imageAlignment?: HorizontalAlignment;
    mobileImageAlignment?: HorizontalAlignment;
    mobileImagePosition?: CardImagePosition;
    overlapped?: boolean;
}

export interface ImageCardImageIndentBasic extends LayoutWidth
{
    indentImageAfter?: number;
    indentImageBefore?: number;
    indentImageLeft?: number;
    indentImageRight?: number;
    indentPaddingImage?: number;
}

export interface ImageCardLayoutBasic extends LayoutWidth
{
    crossAxisOffset?: number;
    mainAxisOffset?: number;
    indentAfter?: number;
    indentBefore?: number;
    indentLeft?: number;
    indentRight?: number; 
}

export type ImageCardProps =
    Omit<React.ComponentProps<typeof Card>, "layout">
    &
    {
        contentProps?: React.ComponentProps<typeof PageContentBlock>;
        imageProps?: React.ComponentProps<typeof VadImage>;
        imageNode?: React.ReactElement<React.ComponentProps<"figure">>;
        mobileImagePosition?: CardImagePosition;
        layout?: MediaSpecific<ImageCardLayoutBasic>;
        imageIndent?: MediaSpecific<ImageCardImageIndentBasic>;
        sideProps: ImageCardAlignProps;
        imagePosition: CardImagePosition;
        animation?: { src: string, textOverAnimation?: boolean };
        isHeroSection?: boolean;
        heroImageAlignment?: HeroCardImageAlignment;
        heroImageSize?: HeroCardImageSize;
        badgeText?: string;
        badgeBackground?: string;
    }

export const ImageCard = ({ patent, children, className, contentProps, imageNode, imagePosition, mobileImagePosition = CardImagePosition.Bottom, imageProps, layout, imageIndent, sideProps, style, animation, isHeroSection, heroImageAlignment = HeroCardImageAlignment.Center, heroImageSize, badgeText = undefined, badgeBackground = 'bg-transparent', ...props }: ImageCardProps): React.ReactComponentElement<typeof Card, ImageCardProps> =>
{
    const content = !isReactNodeEmpty(children) ? <div className={css["content"]}>{children}</div> : contentProps !== undefined ? <PageContentBlock skipEmpty {...contentProps} className={clsx(css["content"], contentProps.className, animation?.textOverAnimation && 'absolute')} /> : null;

    const cardLayout: MediaSpecific<CardLayout> | undefined = layout;

    const styles: React.CSSProperties = {
        ["--content__cross-axis-offset" as any]: `${layout?.all?.crossAxisOffset ?? 0}px`,
        ["--content__cross-axis-offset--ipad" as any]: `${layout?.ipad?.crossAxisOffset ?? layout?.all?.crossAxisOffset ?? 0}px`,
        ["--content__cross-axis-offset--desktop" as any]: `${layout?.desktop?.crossAxisOffset ?? layout?.ipad?.crossAxisOffset ?? layout?.all?.crossAxisOffset ?? 0}px`,
        ["--content__main-axis-offset" as any]: `${layout?.all?.mainAxisOffset ?? 0}px`,
        ["--content__main-axis-offset--ipad" as any]: `${layout?.ipad?.mainAxisOffset ?? layout?.all?.mainAxisOffset ?? 0}px`,
        ["--content__main-axis-offset--desktop" as any]: `${layout?.desktop?.mainAxisOffset ?? layout?.ipad?.mainAxisOffset ?? layout?.all?.mainAxisOffset ?? 0}px`,
        ["--content__indent-after" as any]: `${layout?.all?.indentAfter ?? 0}px`,
        ["--content__indent-after--ipad" as any]: `${layout?.ipad?.indentAfter ?? layout?.all?.indentAfter ?? 0}px`,
        ["--content__indent-after--desktop" as any]: `${layout?.desktop?.indentAfter ?? layout?.ipad?.indentAfter ?? layout?.all?.indentAfter ?? 0}px`,
        ["--content__indent-before" as any]: `${layout?.all?.indentBefore ?? 0}px`,
        ["--content__indent-before--ipad" as any]: `${layout?.ipad?.indentBefore ?? layout?.all?.indentBefore ?? 0}px`,
        ["--content__indent-before--desktop" as any]: `${layout?.desktop?.indentBefore ?? layout?.ipad?.indentBefore ?? layout?.all?.indentBefore ?? 0}px`,
        ["--content__indent-right" as any]: `${layout?.all?.indentRight ?? 0}px`,
        ["--content__indent-right--ipad" as any]: `${layout?.ipad?.indentRight ?? layout?.all?.indentRight ?? 0}px`,
        ["--content__indent-right--desktop" as any]: `${layout?.desktop?.indentRight ?? layout?.ipad?.indentRight ?? layout?.all?.indentRight ?? 0}px`,
        ["--content__indent-left" as any]: `${layout?.all?.indentLeft ?? 0}px`,
        ["--content__indent-left--ipad" as any]: `${layout?.ipad?.indentLeft ?? layout?.all?.indentLeft ?? 0}px`,
        ["--content__indent-left--desktop" as any]: `${layout?.desktop?.indentLeft ?? layout?.ipad?.indentLeft ?? layout?.all?.indentLeft ?? 0}px`,
        ["--content__indent-image-after" as any]: `${imageIndent?.all?.indentImageAfter ?? 0}px`,
        ["--content__indent-image-after--mobile" as any]: `${imageIndent?.mobile?.indentImageAfter ?? imageIndent?.all?.indentImageAfter ?? 0}px`,
        ["--content__indent-image-after--ipad" as any]: `${imageIndent?.mobile?.indentImageAfter ?? imageIndent?.ipad?.indentImageAfter ?? imageIndent?.all?.indentImageAfter ?? 0}px`,
        ["--content__indent-image-after--desktop" as any]: `${imageIndent?.mobile?.indentImageAfter ?? imageIndent?.desktop?.indentImageAfter ?? imageIndent?.ipad?.indentImageAfter ?? imageIndent?.all?.indentImageAfter ?? 0}px`,
        ["--content__indent-image-before" as any]: `${imageIndent?.all?.indentImageBefore ?? 0}px`,
        ["--content__indent-image-before--mobile" as any]: `${imageIndent?.mobile?.indentImageBefore ?? imageIndent?.all?.indentImageBefore ?? 0}px`,
        ["--content__indent-image-before--ipad" as any]: `${imageIndent?.mobile?.indentImageBefore ?? imageIndent?.ipad?.indentImageBefore ?? imageIndent?.all?.indentImageBefore ?? 0}px`,
        ["--content__indent-image-before--desktop" as any]: `${imageIndent?.mobile?.indentImageBefore ?? imageIndent?.desktop?.indentImageBefore ?? imageIndent?.ipad?.indentImageBefore ?? imageIndent?.all?.indentImageBefore ?? 0}px`,
        ["--content__indent-image-right" as any]: `${imageIndent?.all?.indentImageRight ?? 0}px`,
        ["--content__indent-image-right--mobile" as any]: `${imageIndent?.mobile?.indentImageRight ?? imageIndent?.all?.indentImageRight ?? 0}px`,
        ["--content__indent-image-right--ipad" as any]: `${imageIndent?.mobile?.indentImageRight ?? imageIndent?.ipad?.indentImageRight ?? imageIndent?.all?.indentImageRight ?? 0}px`,
        ["--content__indent-image-right--desktop" as any]: `${imageIndent?.mobile?.indentImageRight ?? imageIndent?.desktop?.indentImageRight ?? imageIndent?.ipad?.indentImageRight ?? imageIndent?.all?.indentImageRight ?? 0}px`,
        ["--content__indent-image-left" as any]: `${imageIndent?.all?.indentImageLeft ?? 0}px`,
        ["--content__indent-image-left--mobile" as any]: `${imageIndent?.mobile?.indentImageLeft ?? imageIndent?.all?.indentImageLeft ?? 0}px`,
        ["--content__indent-image-left--ipad" as any]: `${imageIndent?.mobile?.indentImageLeft ?? imageIndent?.ipad?.indentImageLeft ?? imageIndent?.all?.indentImageLeft ?? 0}px`,
        ["--content__indent-image-left--desktop" as any]: `${imageIndent?.mobile?.indentImageLeft ?? imageIndent?.desktop?.indentImageLeft ?? imageIndent?.ipad?.indentImageLeft ?? imageIndent?.all?.indentImageLeft ?? 0}px`,
        ["--content__indent-padding-image" as any]: `${imageIndent?.all?.indentPaddingImage ?? 0}px`,
        ...style,
    };
    // TODO: Maybe transform to classes?

    const classes = clsx(
        css["image-card"],
        {
            [css["image-right"]]: imagePosition === CardImagePosition.Right,
            [css["image-left"]]: imagePosition === CardImagePosition.Left,
            [css["image-top"]]: imagePosition === CardImagePosition.Top,
            [css["image-bottom"]]: imagePosition === CardImagePosition.Bottom,
            [css["image-bottom--mobile"]]: mobileImagePosition === CardImagePosition.Bottom,
            [css["image-align-center"]]: sideProps.imageAlignment === HorizontalAlignment.Center,
            [css["image-align-left"]]: sideProps.imageAlignment === HorizontalAlignment.Left,
            [css["image-align-right"]]: sideProps.imageAlignment === HorizontalAlignment.Right,
            [css["image-align-center--mobile"]]: sideProps.mobileImageAlignment === HorizontalAlignment.Center,
            [css["image-align-left--mobile"]]: sideProps.mobileImageAlignment === HorizontalAlignment.Left,
            [css["image-align-right--mobile"]]: sideProps.mobileImageAlignment === HorizontalAlignment.Right,
            [css["overlapped"]]: sideProps.overlapped,
        },
        className
    );

    return (
        <Card {...props} className={classes} layout={cardLayout} style={styles}>
            {badgeText && (
                <div className={`rounded-full border border-warm-gray-copy text-warm-gray-copy text-[12px] tracking-widest font-semibold px-3.5 py-1 uppercase w-fit mx-auto mt-16 ${badgeBackground}`}>
                    {badgeText}</div>
            )}
            {content}
            {
                imageNode !== undefined ? imageNode : imageProps !== undefined && animation === undefined ? <VadImage {...imageProps} /> : null
            }
            {
                animation !== undefined ?
                    <InplaceVideo autoPlay loop muted className={clsx(animation?.textOverAnimation && 'pt-16 md:pt-52 lg:pt-10 xl:pt-0')}>
                        <source src={animation.src ?? `/video/power-pulse.mp4`} type="video/mp4"/>
                    </InplaceVideo>
                    : null
            }
            {patent && <Patent patent={patent} isInBox={true}/>}
        </Card>
    );
}
